// src/pages/Settings/company/forms/FormStatusesTab.jsx
//
// Task 24888 - "we need custom statuses for the forms ... like job status then
// we map to draft stage (hard coded) or published (hard coded). so scenario. we
// want internally, draft (draft), awaiting info (draft), internal review
// (draft), awaiting signoff (draft), live (publish)".
//
// THE ONE IDEA THIS SCREEN IS BUILT AROUND
// A status has a NAME the company chose and a STAGE the software understands.
// Four of the five statuses in Paul's example are "not live yet" — squashing
// them into the single word "draft" is what loses the information. So the name
// is free, and the stage is a two-way switch that decides one thing only:
// whether the public can reach the form.
//
// That is why the stage control is worded as a consequence ("the public can
// fill this in") rather than as a label ("published"). The label is the bit the
// company already chose; what they need telling is what it will DO.

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Button, Input, Label } from "reactstrap";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import AppModal from "../../../../Components/Common/AppModal";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import {
  formStatusList,
  formStatusCreate,
  formStatusCreateSuccess,
  formStatusUpdate,
  formStatusUpdateSuccess,
  formStatusDelete,
  formStatusDeleteSuccess,
} from "../../../../store/forms/action";

const STAGES = [
  {
    value: "draft",
    label: "Not live",
    help: "The form is being worked on. The public link does not work.",
  },
  {
    value: "published",
    label: "Live",
    help: "The public link works and anybody with it can submit the form.",
  },
];

const emptyRow = () => ({
  Id: null,
  Name: "",
  Description: "",
  Stage: "draft",
  CssColourBackground: "#E2E8F0",
  CssColourText: "#334155",
  Order: 0,
  FlagDefault: 0,
  FlagActive: 1,
});

const FormStatusesTab = () => {
  const dispatch = useDispatch();

  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const {
    list,
    listLoading,
    created,
    updated,
    removed,
    busy,
  } = useSelector((s) => ({
    list: s.Forms?.formStatusList?.formstatus_list || [],
    listLoading: s.Forms?.formStatusListLoading,
    created: s.Forms?.formStatusCreate,
    updated: s.Forms?.formStatusUpdate,
    removed: s.Forms?.formStatusDelete,
    busy:
      s.Forms?.formStatusCreateLoading ||
      s.Forms?.formStatusUpdateLoading ||
      s.Forms?.formStatusDeleteLoading,
  }));

  const refresh = () => dispatch(formStatusList());

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One effect per mutation rather than one shared one, because each needs a
  // different thing closed afterwards.
  useEffect(() => {
    if (isEmpty(created)) return;
    if (created?.status === 200) {
      toast.success(created?.message || "Status added");
      setEditing(null);
      refresh();
    } else {
      toast.error(created?.message || "Could not add the status");
    }
    dispatch(formStatusCreateSuccess([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created]);

  useEffect(() => {
    if (isEmpty(updated)) return;
    if (updated?.status === 200) {
      toast.success(updated?.message || "Status saved");
      setEditing(null);
      refresh();
    } else {
      toast.error(updated?.message || "Could not save the status");
    }
    dispatch(formStatusUpdateSuccess([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updated]);

  useEffect(() => {
    if (isEmpty(removed)) return;
    if (removed?.status === 200) {
      toast.success(removed?.message || "Status deleted");
      setDeleteTarget(null);
      refresh();
    } else {
      // The backend refuses to delete a status that forms are sitting on, and
      // says how many. Surfacing that message rather than a generic failure is
      // the whole value of it.
      toast.error(removed?.message || "Could not delete the status");
      setDeleteTarget(null);
    }
    dispatch(formStatusDeleteSuccess([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removed]);

  const rows = useMemo(
    () =>
      [...(list || [])].sort(
        (a, b) =>
          Number(a.Order ?? 0) - Number(b.Order ?? 0) ||
          Number(a.Id) - Number(b.Id)
      ),
    [list]
  );

  const save = () => {
    if (!String(editing?.Name || "").trim()) {
      toast.error("Give the status a name");
      return;
    }
    const payload = {
      Name: String(editing.Name).trim(),
      Description: editing.Description || "",
      Stage: editing.Stage === "published" ? "published" : "draft",
      CssColourBackground: editing.CssColourBackground,
      CssColourText: editing.CssColourText,
      Order: Number(editing.Order) || 0,
      FlagDefault: editing.FlagDefault ? 1 : 0,
      FlagActive: editing.FlagActive ? 1 : 0,
    };
    if (editing.Id) {
      dispatch(formStatusUpdate([{ Id: editing.Id, ...payload }]));
    } else {
      dispatch(formStatusCreate([payload]));
    }
  };

  const set = (key) => (e) => {
    const value =
      e.target.type === "checkbox" ? (e.target.checked ? 1 : 0) : e.target.value;
    setEditing((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="text-muted" style={{ fontSize: 13 }}>
          Name the stages a form goes through. Each one says whether the public
          can fill the form in — that is the only thing the software reads.
        </div>
        <Button color="primary" size="sm" onClick={() => setEditing(emptyRow())}>
          <i className="fa-solid fa-plus me-2" />
          Add Status
        </Button>
      </div>

      {listLoading && isEmpty(rows) ? (
        <LoadingSpinner />
      ) : isEmpty(rows) ? (
        <div className="text-center p-4">
          <h5>No form statuses yet</h5>
          <p className="text-muted">
            Until you add some, forms carry the built-in Draft and Published and
            behave exactly as they always have.
          </p>
        </div>
      ) : (
        <div className="table-responsive">
          <Table className="table-sm align-middle mb-0">
            <thead className="text-muted">
              <tr>
                <th style={{ width: 70 }}>Order</th>
                <th>Status</th>
                <th>Public can fill it in</th>
                <th>Default</th>
                <th>Active</th>
                <th className="text-end">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.Id}>
                  <td className="text-muted">{row.Order ?? 0}</td>
                  <td>
                    <span
                      className="badge"
                      style={{
                        background: row.CssColourBackground || "#E2E8F0",
                        color: row.CssColourText || "#334155",
                      }}
                    >
                      {row.Name}
                    </span>
                    {row.Description ? (
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        {row.Description}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    {row.Stage === "published" ? (
                      <span className="text-success fw-medium">
                        <i className="ri-global-line align-bottom me-1" />
                        Yes — live
                      </span>
                    ) : (
                      <span className="text-muted">No — not live</span>
                    )}
                  </td>
                  <td>
                    {Number(row.FlagDefault) === 1 ? (
                      <span className="badge bg-info-subtle text-info">
                        New forms start here
                      </span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    {Number(row.FlagActive) === 1 ? (
                      <span className="text-success">Active</span>
                    ) : (
                      <span className="text-muted">Hidden</span>
                    )}
                  </td>
                  <td className="text-end">
                    <Button
                      color="light"
                      size="sm"
                      className="me-2"
                      onClick={() => setEditing({ ...row })}
                    >
                      <i className="ri-pencil-fill align-bottom" />
                    </Button>
                    <Button
                      color="light"
                      size="sm"
                      onClick={() => setDeleteTarget(row)}
                    >
                      <i className="ri-delete-bin-fill align-bottom text-danger" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <AppModal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing?.Id ? "Edit status" : "Add status"}
        headerAccent="indigo"
        size="sm"
        loading={busy}
        onSubmit={save}
        submitLabel={editing?.Id ? "Save" : "Add"}
      >
        {editing ? (
          <>
            <div className="mb-3">
              <Label className="form-label">Name</Label>
              <Input
                value={editing.Name}
                onChange={set("Name")}
                placeholder="e.g. Awaiting signoff"
              />
            </div>

            <div className="mb-3">
              <Label className="form-label">Description</Label>
              <Input
                type="textarea"
                rows={2}
                value={editing.Description || ""}
                onChange={set("Description")}
                placeholder="What this stage means, for whoever comes next"
              />
            </div>

            <div className="mb-3">
              <Label className="form-label">Can the public fill it in?</Label>
              <Input type="select" value={editing.Stage} onChange={set("Stage")}>
                {STAGES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Input>
              <div className="form-text">
                {STAGES.find((s) => s.value === editing.Stage)?.help}
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-4">
                <Label className="form-label">Order</Label>
                <Input
                  type="number"
                  value={editing.Order ?? 0}
                  onChange={set("Order")}
                />
              </div>
              <div className="col-4">
                <Label className="form-label">Background</Label>
                <Input
                  type="color"
                  value={editing.CssColourBackground || "#E2E8F0"}
                  onChange={set("CssColourBackground")}
                />
              </div>
              <div className="col-4">
                <Label className="form-label">Text</Label>
                <Input
                  type="color"
                  value={editing.CssColourText || "#334155"}
                  onChange={set("CssColourText")}
                />
              </div>
            </div>

            <div className="form-check mb-2">
              <Input
                type="checkbox"
                className="form-check-input"
                id="formstatus-default"
                checked={Boolean(Number(editing.FlagDefault))}
                onChange={set("FlagDefault")}
              />
              <Label className="form-check-label" for="formstatus-default">
                New forms start on this status
              </Label>
            </div>

            <div className="form-check">
              <Input
                type="checkbox"
                className="form-check-input"
                id="formstatus-active"
                checked={Boolean(Number(editing.FlagActive))}
                onChange={set("FlagActive")}
              />
              <Label className="form-check-label" for="formstatus-active">
                Active — offer this status when changing a form
              </Label>
            </div>
          </>
        ) : null}
      </AppModal>

      <DeleteModal
        show={Boolean(deleteTarget)}
        loading={busy}
        customText={
          deleteTarget
            ? `Delete the status "${deleteTarget.Name}"? Forms already using it will block the delete.`
            : ""
        }
        onCloseClick={() => setDeleteTarget(null)}
        onDeleteClick={() => dispatch(formStatusDelete(deleteTarget.Id))}
      />
    </>
  );
};

export default FormStatusesTab;
